import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, Search, X, ChevronDown, ChevronUp,
  Undo2, ExternalLink, Info, Download,
} from 'lucide-react';
import settlementReportService from '../../../services/settlementReportService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (val) => {
  const n = Number(val ?? 0);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}₹${Math.abs(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const fmtPct = (val) => (val == null ? '—' : `${Number(val).toFixed(1)}%`);
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const marginTone = (pct) => {
  const n = Number(pct);
  if (Number.isNaN(n)) return 'text-slate-500';
  if (n >= 70) return 'text-emerald-700 font-bold';
  if (n >= 50) return 'text-emerald-600';
  if (n >= 30) return 'text-amber-600';
  return 'text-rose-600 font-bold';
};

// ═════════════════════════════════════════════════════════════════════════════

export default function SettlementOrderPnl({ sellerAccountId, from, to, settlements }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');
  const [selectedSettlements, setSelectedSettlements] = useState(new Set()); // empty = all
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!sellerAccountId || !from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    settlementReportService.getOrderPnlByRange(sellerAccountId, from, to)
      .then((res) => { if (!cancelled) setRows(Array.isArray(res.data) ? res.data : (res || [])); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load order P&L');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sellerAccountId, from, to]);

  const toggleSettlement = (sid) => {
    setSelectedSettlements(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let out = rows;

    // Settlement-id chip filter (frontend-side; rows carry settlementIds[])
    if (selectedSettlements.size > 0) {
      out = out.filter(r =>
        (r.settlementIds || []).some(s => selectedSettlements.has(s))
      );
    }

    if (filter === 'refunded') out = out.filter(r => r.refunds?.hasRefund);
    else if (filter === 'lowMargin') {
      // Exclude refunds — they are always negative-margin and drown out the genuine
      // low-margin signal from non-refund orders the seller actually wants to investigate.
      out = out.filter(r => Number(r.marginPct) < 30 && !r.refunds?.hasRefund);
    }

    if (fulfillmentFilter === 'fba') out = out.filter(r => r.fulfillment === 'AFN');
    else if (fulfillmentFilter === 'fbm') out = out.filter(r => r.fulfillment === 'MFN');

    if (search) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        r.orderId?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        (r.skus || []).some(s => s.toLowerCase().includes(q)) ||
        r.shipCity?.toLowerCase().includes(q) ||
        r.shipState?.toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      const get = (r) => {
        if (sortBy === 'orderDate') return r.orderDate || '';
        if (sortBy === 'customerPaid') return Number(r.revenue?.customerPaid) || 0;
        if (sortBy === 'totalFees') return Number(r.fees?.totalFees) || 0;
        if (sortBy === 'netInHand') return Number(r.netInHand) || 0;
        if (sortBy === 'marginPct') return Number(r.marginPct) || 0;
        return r[sortBy];
      };
      const va = get(a), vb = get(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, filter, fulfillmentFilter, search, sortBy, sortDir, selectedSettlements]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, r) => ({
      customerPaid: acc.customerPaid + (Number(r.revenue?.customerPaid) || 0),
      totalFees:    acc.totalFees + (Number(r.fees?.totalFees) || 0),
      taxWithheld:  acc.taxWithheld + (Number(r.taxWithheld?.totalWithheld) || 0),
      netInHand:    acc.netInHand + (Number(r.netInHand) || 0),
    }), { customerPaid: 0, totalFees: 0, taxWithheld: 0, netInHand: 0 });
  }, [filtered]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const handleExportCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      'Date', 'Order ID', 'SKU', 'Description', 'Qty', 'Fulfilment',
      'Ship City', 'Ship State', 'Settlements',
      'Product Sales', 'Shipping Charged', 'GST Collected', 'Customer Paid',
      'Promo', 'Selling Fees', 'FBA Fees', 'Easy Ship Fees', 'Total Fees',
      'TCS', 'TDS', 'Refund', 'FBA Fee Refund',
      'Net in Hand', 'Margin %',
    ];
    const csvRows = filtered.map(r => [
      r.orderDate, r.orderId, (r.skus || []).join('; '), `"${(r.description || '').replace(/"/g, '""')}"`,
      r.quantity, r.fulfillment, r.shipCity || '', r.shipState || '',
      (r.settlementIds || []).join('; '),
      r.revenue?.productSales || 0, r.revenue?.shippingCharged || 0, r.revenue?.salesTaxCollected || 0,
      r.revenue?.customerPaid || 0,
      r.promotionalRebates || 0,
      r.fees?.sellingFees || 0, r.fees?.fbaFees || 0, r.fees?.easyShipFees || 0, r.fees?.totalFees || 0,
      r.taxWithheld?.tcs || 0, r.taxWithheld?.tds || 0,
      r.refunds?.amount || 0, r.refunds?.fbaFeeRefund || 0,
      r.netInHand || 0, r.marginPct ?? '',
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-pnl_${from}_to_${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-400">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle size={28} className="mx-auto text-rose-500 mb-2" />
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm font-semibold text-slate-700">No orders in this date range</p>
        <p className="text-xs text-slate-500 mt-1">Try a different range or sync new settlements.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 space-y-3">
      {/* ─── Filter bar ──────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order, SKU, city…"
              className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-[220px]"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={12} />
              </button>
            )}
          </div>

          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
            All ({rows.length})
          </FilterChip>
          <FilterChip active={filter === 'refunded'} onClick={() => setFilter('refunded')}>
            Refunded ({rows.filter(r => r.refunds?.hasRefund).length})
          </FilterChip>
          <FilterChip
            active={filter === 'lowMargin'}
            onClick={() => setFilter('lowMargin')}
            title="Orders with margin under 30% — refunds excluded so genuine thin-margin orders surface"
          >
            Low margin &lt;30% (
            {rows.filter(r => Number(r.marginPct) < 30 && !r.refunds?.hasRefund).length}
            )
          </FilterChip>

          <span className="w-px h-5 bg-slate-200 mx-1" />

          <FilterChip active={fulfillmentFilter === 'all'} onClick={() => setFulfillmentFilter('all')}>
            All Fulfilment
          </FilterChip>
          <FilterChip active={fulfillmentFilter === 'fba'} onClick={() => setFulfillmentFilter('fba')}>
            FBA ({rows.filter(r => r.fulfillment === 'AFN').length})
          </FilterChip>
          <FilterChip active={fulfillmentFilter === 'fbm'} onClick={() => setFulfillmentFilter('fbm')}>
            FBM ({rows.filter(r => r.fulfillment === 'MFN').length})
          </FilterChip>

          <div className="flex-1" />

          <button
            onClick={handleExportCsv}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <Download size={11} /> Export CSV
          </button>
        </div>

        {/* Settlement chips — for filtering by COD vs Electronic disbursement */}
        {settlements && settlements.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-100">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mr-1">
              Settlement:
            </span>
            <FilterChip active={selectedSettlements.size === 0} onClick={() => setSelectedSettlements(new Set())}>
              All ({settlements.length})
            </FilterChip>
            {settlements.map(s => (
              <FilterChip
                key={s.settlementId}
                active={selectedSettlements.has(s.settlementId)}
                onClick={() => toggleSettlement(s.settlementId)}
                title={`${s.settlementId} · ${fmt(s.depositAmount)}`}
              >
                ID …{(s.settlementId || '').slice(-5)} · {fmt(s.depositAmount)}
              </FilterChip>
            ))}
          </div>
        )}
      </div>

      {/* ─── Header explainer ─────────────────────────────── */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] text-blue-800 flex items-start gap-2">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          <strong>Net in hand</strong> = customer paid − Amazon fees − tax withheld − refunds.
          For self-fulfilled (FBM) orders, the Easy Ship charge appears as a separate transaction —
          we automatically join it back to the order so you see the true take-home.
        </span>
      </div>

      {/* ─── Table ───────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <Th></Th>
                <Th sortable sortBy={sortBy} sortDir={sortDir} field="orderDate" onSort={handleSort}>Date</Th>
                <Th>Order / SKU</Th>
                <Th>Fulfilment</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="customerPaid" onSort={handleSort}>Customer Paid</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="totalFees" onSort={handleSort}>Fees</Th>
                <Th right>Tax W/H</Th>
                <Th right>Refund</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="netInHand" onSort={handleSort}>Net in Hand</Th>
                <Th right sortable sortBy={sortBy} sortDir={sortDir} field="marginPct" onSort={handleSort}>Margin</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <React.Fragment key={r.orderId}>
                  <tr
                    className={`hover:bg-slate-50/50 cursor-pointer ${expandedId === r.orderId ? 'bg-[#FFF9EE]' : ''}`}
                    onClick={() => setExpandedId(expandedId === r.orderId ? null : r.orderId)}
                  >
                    <td className="px-2 py-2">
                      {expandedId === r.orderId
                        ? <ChevronUp size={12} className="text-slate-400" />
                        : <ChevronDown size={12} className="text-slate-400" />}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.orderDate)}</td>
                    <td className="px-3 py-2 max-w-[260px]">
                      <p className="font-mono text-[11px] font-semibold text-slate-700 truncate" title={r.orderId}>
                        {r.orderId}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate" title={r.description}>
                        {(r.skus || []).join(', ') || '—'}
                        {r.quantity > 1 && <span className="text-[#C45500] ml-1">×{r.quantity}</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                        r.fulfillment === 'AFN'
                          ? 'bg-violet-50 text-violet-700 border-violet-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {r.fulfillment === 'AFN' ? 'FBA' : r.fulfillment === 'MFN' ? 'FBM' : (r.fulfillment || '—')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                      {fmt(r.revenue?.customerPaid)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-600">
                      {fmt(r.fees?.totalFees)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                      {fmt(r.taxWithheld?.totalWithheld)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.refunds?.hasRefund
                        ? <span className="text-rose-700 font-semibold">{fmt(r.refunds.amount)}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${
                      Number(r.netInHand) < 0 ? 'text-rose-700 font-bold' : 'font-bold text-emerald-700'
                    }`}>
                      {fmt(r.netInHand)}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${marginTone(r.marginPct)}`}>
                      {fmtPct(r.marginPct)}
                    </td>
                  </tr>
                  {expandedId === r.orderId && (
                    <tr>
                      <td colSpan={10} className="px-4 py-3 bg-[#FFF9EE]/50 border-t border-[#FFD89E]">
                        <ExpandedDetail row={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-xs font-bold text-slate-700">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500">
                  Totals ({filtered.length} orders)
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(totals.customerPaid)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmt(totals.totalFees)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt(totals.taxWithheld)}</td>
                <td className="px-3 py-2"></td>
                <td className={`px-3 py-2 text-right tabular-nums ${totals.netInHand < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {fmt(totals.netInHand)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {totals.customerPaid > 0
                    ? `${((totals.netInHand / totals.customerPaid) * 100).toFixed(1)}%`
                    : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FilterChip({ active, onClick, children, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors whitespace-nowrap ${
        active
          ? 'bg-[#FFF4E6] border-[#f08804] text-[#C45500]'
          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

function Th({ children, right, sortable, sortBy, sortDir, field, onSort }) {
  const cls = `px-3 py-2 ${right ? 'text-right' : 'text-left'} ${sortable ? 'cursor-pointer hover:text-slate-700' : ''}`;
  if (!sortable) return <th className={cls}>{children}</th>;
  return (
    <th className={cls} onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-0.5">
        {children}
        {sortBy === field && (sortDir === 'asc' ? '▲' : '▼')}
      </span>
    </th>
  );
}

function ExpandedDetail({ row }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-1">
      <DetailBlock title="Revenue (what customer paid)" tone="emerald">
        <DetailLine label="Product" value={fmt(row.revenue?.productSales)} />
        <DetailLine label="Shipping charged" value={fmt(row.revenue?.shippingCharged)} />
        <DetailLine label="GST collected" value={fmt(row.revenue?.salesTaxCollected)} />
        <DetailLine label="Promo discount" value={fmt(row.promotionalRebates)} />
        <DetailLine label="Total" value={fmt(row.revenue?.customerPaid)} bold />
      </DetailBlock>

      <DetailBlock title="Amazon kept" tone="rose">
        <DetailLine label="Selling fees" value={fmt(row.fees?.sellingFees)} />
        <DetailLine label="FBA fees" value={fmt(row.fees?.fbaFees)} />
        <DetailLine label="Easy Ship" value={fmt(row.fees?.easyShipFees)}
          hint={row.fees?.hasSeparateEasyShipRow ? 'joined from separate row' : undefined} />
        <DetailLine label="Total fees" value={fmt(row.fees?.totalFees)} bold />
      </DetailBlock>

      <DetailBlock title="Tax + Refunds" tone="amber">
        <DetailLine label="TCS withheld" value={fmt(row.taxWithheld?.tcs)} />
        <DetailLine label="TDS withheld" value={fmt(row.taxWithheld?.tds)} />
        {row.refunds?.hasRefund && (
          <DetailLine label="Refund" value={fmt(row.refunds.amount)} icon={<Undo2 size={10} />} />
        )}
        {Number(row.refunds?.fbaFeeRefund) !== 0 && (
          <DetailLine label="FBA fee refund" value={fmt(row.refunds.fbaFeeRefund)} />
        )}
        <DetailLine label="Net in hand" value={fmt(row.netInHand)} bold tone="emerald" />
      </DetailBlock>

      <div className="sm:col-span-3 flex items-center gap-3 text-[10px] text-slate-500 pt-2 border-t border-amber-200/40">
        <span><strong>City:</strong> {row.shipCity || '—'}</span>
        <span><strong>State:</strong> {row.shipState || '—'}</span>
        {(row.settlementIds || []).length > 0 && (
          <span><strong>Settlement:</strong> {(row.settlementIds || []).join(', ')}</span>
        )}
        <a
          href={`https://sellercentral.amazon.in/orders-v3/order/${row.orderId}`}
          target="_blank" rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-[#f08804] hover:text-[#C45500] font-semibold"
        >
          <ExternalLink size={10} /> Open in Seller Central
        </a>
      </div>
    </div>
  );
}

function DetailBlock({ title, tone, children }) {
  const head = {
    emerald: 'text-emerald-700 border-emerald-200',
    rose: 'text-rose-700 border-rose-200',
    amber: 'text-amber-700 border-amber-200',
  }[tone] || 'text-slate-700 border-slate-200';
  return (
    <div>
      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 pb-1 border-b ${head}`}>{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailLine({ label, value, bold, hint, tone, icon }) {
  const valueTone = bold
    ? (tone === 'emerald' ? 'text-emerald-700 font-bold' : 'text-slate-900 font-bold')
    : 'text-slate-700';
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="text-slate-500 truncate flex items-center gap-1">
        {icon} {label}
        {hint && <span className="text-[9px] italic text-slate-400">({hint})</span>}
      </span>
      <span className={`tabular-nums ${valueTone}`}>{value}</span>
    </div>
  );
}
